import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { faDownload, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Spinner } from '../../Spinner';
import NotifyPaymentModal from './NotifyPaymentModal';
import MarkPaidButton from './MarkPaidButton';
import { BillingInfoHeader, isCouponMethod } from './BillingInfoHeader';
import { APIClient } from '../../../utils/APIClient';
import { API_URL } from '../../../settings/local_settings';

function PaymentStatusBadge({ liquidation }) {
    const { t } = useTranslation();
    const isPaid = liquidation.payment_status_manual === 'Pago' || liquidation.payment_status_auto === 'Pago';

    if (isPaid) {
        return (
            <span style={{
                backgroundColor: '#7CB502',
                color: '#fff',
                padding: '0.15em 0.5em',
                borderRadius: '0.3em',
                fontSize: '0.75em',
                fontWeight: 600,
                fontFamily: 'Source Sans Pro, sans-serif'
            }}>
                {t('agency_subscription.paid')}
            </span>
        );
    }

    return (
        <span style={{
            backgroundColor: '#EC9F00',
            color: '#fff',
            padding: '0.15em 0.5em',
            borderRadius: '0.3em',
            fontSize: '0.75em',
            fontWeight: 600,
            fontFamily: 'Source Sans Pro, sans-serif'
        }}>
            {t('agency_subscription.pending_payment')}
        </span>
    );
}

// La usa también la vista de la agencia paga (`paid/PaymentsCard`), en la columna que ve sólo el superadmin.
export function CouponStatusCell({ cupons }) {
    const { t } = useTranslation();

    if (!cupons || cupons.length === 0) {
        return <span style={{ color: '#B6B6B7' }}>-</span>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3em' }}>
            {cupons.map((cupon, idx) => {
                const statusColors = {
                    paid: '#7CB502',
                    pending: '#EC9F00',
                    cancelled: '#B6B6B7'
                };
                const statusLabels = {
                    paid: t('agency_subscription.paid'),
                    pending: t('agency_subscription.pending_payment'),
                    cancelled: t('agency_subscription.coupon_cancelled')
                };
                const color = statusColors[cupon.simplified_status] || '#B6B6B7';
                const label = statusLabels[cupon.simplified_status] || cupon.status;

                return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                        <span style={{
                            backgroundColor: color,
                            color: '#fff',
                            padding: '0.1em 0.4em',
                            borderRadius: '0.2em',
                            fontSize: '0.7em',
                            fontWeight: 600
                        }}>
                            {label}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Link/botón "Pagar" del cupón. Vive en la columna Acciones (antes estaba escondido en
 * la columna "Cupón de pago", solo visible para super admin). `primary` lo muestra como
 * botón naranja sólido (CTA del cliente); `subtle` como link de texto (para el admin,
 * que puede abrir el cupón pero su acción principal es "Marcar como pago").
 */
function PayCouponLink({ url, variant }) {
    const { t } = useTranslation();
    const label = t('agency_subscription.pay_now');

    if (variant === 'primary') {
        return (
            <a
                className="btn -alt"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.7em', padding: '0.2em 0.6em', whiteSpace: 'nowrap', textDecoration: 'none' }}
            >
                {label}
            </a>
        );
    }

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#FF470F', fontSize: '0.78em', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
        >
            {label}
        </a>
    );
}

function formatArgAmount(num, currency) {
    if (num == null) return '-';
    const prefix = currency === 'ARS' ? '$ ' : 'US$ ';
    return prefix + Number(num).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function InvoicesCell({ liquidation }) {
    const { t } = useTranslation();
    const invoices = liquidation.invoices;
    if (!invoices || invoices.length === 0) {
        return <span>{liquidation.colppy_invoice_numbers || '-'}</span>;
    }

    const handleDownload = (inv) => {
        const url = `${API_URL}agency/subscription/invoice/${inv.colppy_invoice_id}/download/`;
        const fallbackName = `factura-${inv.number || inv.colppy_invoice_id}.pdf`;
        new APIClient(url).download('GET', fallbackName, (result) => {
            if (result && result.status === 'ERROR') {
                toast.error(t('agency_subscription.download_invoice.toast_error'));
            }
        });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25em' }}>
            {invoices.map((inv) => (
                <button
                    key={inv.colppy_invoice_id}
                    type="button"
                    onClick={() => handleDownload(inv)}
                    aria-label={t('agency_subscription.download_invoice.aria', { number: inv.number || inv.colppy_invoice_id })}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: '#FF470F',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4em',
                        fontWeight: 500,
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                >
                    {inv.number || inv.colppy_invoice_id}
                    <FontAwesomeIcon icon={faDownload} style={{ fontSize: '0.8em' }} />
                </button>
            ))}
        </div>
    );
}

export function BillingHistorySection({ billingData, onRetry, onEmailsUpdated, showPaymentDetails = false, onLiquidationUpdated, onRefreshBilling, refreshingBilling = false }) {
    const { t } = useTranslation();

    const hasPendingLiquidation = billingData && billingData.liquidations && billingData.liquidations.some(
        (l) => l.payment_status_manual !== 'Pago' && l.payment_status_auto !== 'Pago'
    );

    if (!billingData) {
        return (
            <div className="box" style={{ marginBottom: '1.5em' }}>
                <header className="box-header">
                    <h2 className="box-title">{t('agency_subscription.billing_history')}</h2>
                </header>
                <div style={{ padding: '2em', textAlign: 'center' }}>
                    <Spinner color="positive small" />
                </div>
            </div>
        );
    }

    if (!billingData.available) {
        return (
            <div className="box" style={{ marginBottom: '1.5em' }}>
                <header className="box-header">
                    <h2 className="box-title">{t('agency_subscription.billing_history')}</h2>
                </header>
                <div style={{
                    padding: '2em 1.1em',
                    textAlign: 'center',
                    color: '#B6B6B7',
                    fontFamily: 'Source Sans Pro, sans-serif',
                    fontSize: '0.87em'
                }}>
                    <p style={{ margin: '0 0 0.75em' }}>
                        {billingData.reason === 'tesla_error'
                            ? t('agency_subscription.billing_error')
                            : t('agency_subscription.billing_not_available')
                        }
                    </p>
                    {billingData.reason === 'tesla_error' && onRetry && (
                        <button className="btn -alt" onClick={onRetry} style={{ fontSize: '0.87em' }}>
                            {t('agency_subscription.retry')}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const { active_comm_agree, liquidations } = billingData;
    const showCouponColumn = showPaymentDetails && active_comm_agree && isCouponMethod(active_comm_agree.payment_method);

    return (
        <div className="box" style={{ marginBottom: '1.5em' }}>
            <header className="box-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5em' }}>
                <h2 className="box-title">{t('agency_subscription.billing_history')}</h2>
                {hasPendingLiquidation && onRefreshBilling && (
                    <button
                        type="button"
                        disabled={refreshingBilling}
                        onClick={onRefreshBilling}
                        title={t('agency_subscription.refresh_billing.tooltip')}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '0.3em 0.2em',
                            cursor: refreshingBilling ? 'default' : 'pointer',
                            color: refreshingBilling ? '#B6B6B7' : '#4A4A4A',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            fontFamily: 'Source Sans Pro, sans-serif',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.4em',
                            transition: 'color 0.15s ease',
                        }}
                        onMouseEnter={(e) => { if (!refreshingBilling) e.currentTarget.style.color = '#FF470F'; }}
                        onMouseLeave={(e) => { if (!refreshingBilling) e.currentTarget.style.color = '#4A4A4A'; }}
                    >
                        <FontAwesomeIcon icon={faSyncAlt} spin={refreshingBilling} style={{ fontSize: '0.85em' }} />
                        {refreshingBilling
                            ? t('agency_subscription.refresh_billing.loading')
                            : t('agency_subscription.refresh_billing.cta')}
                    </button>
                )}
            </header>
            <div style={{ padding: '0.75em 1.1em 1.1em' }}>
                {showPaymentDetails ? (
                    <BillingInfoHeader
                        billingData={billingData}
                        onEmailsUpdated={onEmailsUpdated}
                    />
                ) : (
                    active_comm_agree && Object.keys(active_comm_agree).length > 0 && (
                        <div style={{
                            backgroundColor: '#F9F9F9',
                            borderRadius: '0.3em',
                            padding: '0.85em 1em',
                            marginBottom: '1em'
                        }}>
                            <h6 style={{
                                fontFamily: 'Source Sans Pro, sans-serif',
                                fontWeight: 600,
                                fontSize: '0.87em',
                                color: '#4A4A4A',
                                marginBottom: '0.4em'
                            }}>
                                {t('agency_subscription.active_agreement')}
                            </h6>
                            <div style={{
                                display: 'flex',
                                gap: '2em',
                                flexWrap: 'wrap',
                                fontSize: '0.87em',
                                fontFamily: 'Source Sans Pro, sans-serif'
                            }}>
                                {active_comm_agree.currency && (
                                    <div><span style={{ color: '#B6B6B7' }}>{t('agency_subscription.currency')}:</span> <strong style={{ color: '#4A4A4A' }}>{active_comm_agree.currency}</strong></div>
                                )}
                                {active_comm_agree.payment_method && (
                                    <div><span style={{ color: '#B6B6B7' }}>{t('agency_subscription.payment_method_label')}:</span> <strong style={{ color: '#4A4A4A' }}>{active_comm_agree.payment_method}</strong></div>
                                )}
                                {active_comm_agree.fee_text && (
                                    <div><span style={{ color: '#B6B6B7' }}>{t('agency_subscription.fees')}:</span> <strong style={{ color: '#4A4A4A' }}>{active_comm_agree.fee_text}</strong></div>
                                )}
                            </div>
                        </div>
                    )
                )}

                {(!liquidations || liquidations.length === 0) ? (
                    <p style={{
                        textAlign: 'center',
                        color: '#B6B6B7',
                        padding: '1.5em 0',
                        fontFamily: 'Source Sans Pro, sans-serif',
                        fontSize: '0.87em'
                    }}>
                        {t('agency_subscription.no_liquidations')}
                    </p>
                ) : (
                    <div className="table-box">
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>{t('agency_subscription.month')}</th>
                                    <th style={thStyle}>{t('agency_subscription.payment_status')}</th>
                                    {showCouponColumn && (
                                        <th style={thStyle}>{t('agency_subscription.coupon_status')}</th>
                                    )}
                                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('agency_subscription.amount')}</th>
                                    <th style={thStyle}>{t('agency_subscription.invoice')}</th>
                                    <th style={{ ...thStyle, textAlign: 'center' }}>{t('agency_subscription.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {liquidations.map((liq, idx) => {
                                    const isPaid = liq.payment_status_manual === 'Pago' || liq.payment_status_auto === 'Pago';
                                    const payableCupon = (liq.payment_cupons || []).find(
                                        (c) => c.simplified_status === 'pending' && c.payment_url
                                    );
                                    const rowBg = idx % 2 === 0 ? '#fff' : '#F7F7F7';
                                    return (
                                        <tr key={liq.id} style={{ backgroundColor: rowBg }}>
                                            <td style={{ ...tdStyle, fontWeight: 600 }}>
                                                <div>{liq.month_created}</div>
                                                {liq.period_as_text && (
                                                    <div style={{ fontSize: '0.75em', fontWeight: 400, color: '#B6B6B7', marginTop: '0.15em' }}>
                                                        {liq.period_as_text}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={tdStyle}><PaymentStatusBadge liquidation={liq} /></td>
                                            {showCouponColumn && (
                                                <td style={tdStyle}>
                                                    <CouponStatusCell cupons={liq.payment_cupons} />
                                                </td>
                                            )}
                                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                                                {formatArgAmount(liq.amount_in_currency != null ? liq.amount_in_currency : liq.invoice_total, liq.currency)}
                                            </td>
                                            <td style={tdStyle}>
                                                <InvoicesCell liquidation={liq} />
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {!isPaid && (
                                                    <div style={{ display: 'flex', gap: '0.5em', justifyContent: 'center', alignItems: 'center' }}>
                                                        {showPaymentDetails ? (
                                                            <>
                                                                <MarkPaidButton
                                                                    liquidationId={liq.id}
                                                                    onMarked={onLiquidationUpdated}
                                                                />
                                                                {payableCupon && (
                                                                    <PayCouponLink url={payableCupon.payment_url} variant="subtle" />
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                {payableCupon && (
                                                                    <PayCouponLink url={payableCupon.payment_url} variant="primary" />
                                                                )}
                                                                <NotifyPaymentModal
                                                                    liquidationId={liq.id}
                                                                    liquidationMonth={liq.month_created}
                                                                    subtle
                                                                />
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const thStyle = {
    color: '#4A4A4A',
    fontSize: '0.75em',
    fontWeight: 600,
    fontFamily: 'Source Sans Pro, sans-serif',
    height: '3.5em',
    padding: '0 1.25em',
    textAlign: 'left',
    borderBottom: '1px solid #E2E2E2'
};

const tdStyle = {
    color: '#4A4A4A',
    fontSize: '0.87em',
    fontWeight: 400,
    fontFamily: 'Source Sans Pro, sans-serif',
    height: '2.8em',
    padding: '0.4em 1.25em'
};
